import React, { useCallback, useEffect, useMemo, useState } from "react";
import QmsDocumentPreviewPane from "./QmsDocumentPreviewPane";
import QmsDocumentLibraryStaffPanel from "./QmsDocumentLibraryStaffPanel";
import QmsMyLearningAssignmentPanel from "./QmsMyLearningAssignmentPanel";
import QmsDocumentLibraryLearningPanel from "./QmsDocumentLibraryLearningPanel";
import QmsVaultPeoplePanel from "./QmsVaultPeoplePanel";
import QmsVaultAssociatedDocsPanel from "./QmsVaultAssociatedDocsPanel";
import QmsVaultQuizPanel from "./QmsVaultQuizPanel";
import QmsVaultVersionHistoryPanel from "./QmsVaultVersionHistoryPanel";
import QmsVaultAuditLogPanel from "./QmsVaultAuditLogPanel";
import IsfPageTour from "../isf/IsfPageTour";
import "../isf/IsfPageTour.scss";
import { buildQmsDocumentWorkspaceTourSteps } from "./qmsDocumentWorkspaceTourSteps";
import { markQmsWorkspacePanelTourCompleted } from "./qmsDocumentWorkspaceTourStorage";
import { pickApiRowValue } from "./qmsApiUtils";
import { fetchWorkspaceDocumentById } from "./qmsService";
import { getVaultDocumentTitle } from "./qmsVaultUtils";
import {
  buildAssociatedDocPreviewRow,
  getVaultDocumentInfo,
  getVaultWorkflowActions,
  isAssociatedDocPreview,
  isVaultHistoricalPreview,
} from "./qmsVaultWorkspaceUtils";
import {
  WORKSPACE_PANELS,
  WORKSPACE_VAULT_PANELS,
} from "./qmsDocumentWorkspacePanels";

export { WORKSPACE_PANELS };

function QmsDocumentLibraryWorkspaceModal({
  open,
  onClose,
  row,
  userData,
  activePanel = "preview",
  onPanelChange,
  documentTitle,
  documentCode,
  documentUrl,
  documentExtension,
  panels = WORKSPACE_PANELS,
  canManageStaff = false,
  staff = [],
  staffLoading = false,
  savingStaffIds = [],
  onToggleStaff,
  onBulkToggleStaff,
  canManageLearningStaff = false,
  learningStaff = [],
  learningStaffTrainingRoles = [],
  learningStaffLoading = false,
  learningStaffSaving = false,
  onMyLearningSave,
  onMyLearningDirtyChange,
  savingLearningStaffIds = [],
  onToggleLearningStaff,
  onBulkToggleLearningStaff,
  learningProgressStaff = [],
  learningProgressLoading = false,
  remindingStaffIds = [],
  canSendLearningReminders = false,
  onSendLearningReminder,
  onSendBulkLearningReminder,
  staffId = "",
}) {
  const [showWorkspaceTour, setShowWorkspaceTour] = useState(false);
  const [tourPanelId, setTourPanelId] = useState(activePanel);
  const [workspaceResponse, setWorkspaceResponse] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState("");
  const [previewRow, setPreviewRow] = useState(null);

  useEffect(() => {
    if (!open) {
      setShowWorkspaceTour(false);
      setWorkspaceResponse(null);
      setDetailsLoading(false);
      setDetailsError("");
      setPreviewRow(null);
    }
  }, [open]);

  useEffect(() => {
    if (showWorkspaceTour) {
      setShowWorkspaceTour(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- close tour when panel changes
  }, [activePanel]);

  useEffect(() => {
    if (!open || !row?.DocumentId || !userData?.StaffId) {
      return undefined;
    }

    let cancelled = false;
    setDetailsLoading(true);
    setDetailsError("");
    setWorkspaceResponse(null);
    setPreviewRow(null);

    fetchWorkspaceDocumentById(userData, row.DocumentId)
      .then((response) => {
        if (!cancelled) setWorkspaceResponse(response);
      })
      .catch((error) => {
        if (!cancelled) {
          setDetailsError(error?.message || "Could not load document details.");
        }
      })
      .finally(() => {
        if (!cancelled) setDetailsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, row?.DocumentId, userData]);

  const workspaceTourSteps = useMemo(
    () =>
      buildQmsDocumentWorkspaceTourSteps(tourPanelId, {
        canManageStaff,
        hasStaff: staff.length > 0,
      }),
    [tourPanelId, canManageStaff, staff.length],
  );

  const activePanelMeta =
    panels.find((panel) => panel.id === activePanel) ||
    panels[0];

  const vaultDocumentInfo = useMemo(
    () => (workspaceResponse ? getVaultDocumentInfo(workspaceResponse) : {}),
    [workspaceResponse],
  );

  const resolvedDocumentUrl = useMemo(
    () =>
      String(
        previewRow?.DocumentURL ||
          documentUrl ||
          pickApiRowValue(vaultDocumentInfo, "DocumentURL", "documentUrl") ||
          "",
      ).trim(),
    [previewRow, documentUrl, vaultDocumentInfo],
  );

  const resolvedDocumentExtension = useMemo(
    () =>
      previewRow?.DocumentExtension ||
      documentExtension ||
      pickApiRowValue(vaultDocumentInfo, "DocumentExtension", "documentExtension") ||
      "",
    [previewRow, documentExtension, vaultDocumentInfo],
  );

  const readOnlyWorkflow = useMemo(
    () => ({
      ...getVaultWorkflowActions(workspaceResponse || {}),
      canSaveReviewers: false,
      canSendReviewers: false,
      canCompleteReview: false,
      canSaveApprovers: false,
      canSendApprovers: false,
      canCompleteApproval: false,
      canManageAssociatedDocs: false,
      canManageQuiz: false,
    }),
    [workspaceResponse],
  );

  const viewingHistoricalVersion = isVaultHistoricalPreview(previewRow, row);
  const viewingAssociatedDocument = isAssociatedDocPreview(previewRow);
  const isPreviewPanel = activePanel === "preview";
  const isStaffPanel =
    activePanel === "libraryStaff" || activePanel === "learningStaff";
  const isLearningProgressPanel = activePanel === "learningProgress";
  const needsVaultData = WORKSPACE_VAULT_PANELS.has(activePanel);

  const openPanelGuide = () => {
    setTourPanelId(activePanel);
    setShowWorkspaceTour(true);
  };

  const handleTourClose = () => {
    setShowWorkspaceTour(false);
  };

  const handleTourCompleted = (id) => {
    markQmsWorkspacePanelTourCompleted(id, tourPanelId);
  };

  const returnToLatestVersion = useCallback(() => {
    setPreviewRow(null);
    onPanelChange?.("preview");
  }, [onPanelChange]);

  const openAssociatedDocPreview = useCallback(
    (docRow) => {
      setPreviewRow(buildAssociatedDocPreviewRow(docRow));
      onPanelChange?.("preview");
    },
    [onPanelChange],
  );

  const handleNavClick = useCallback(
    (panel) => {
      if (panel.id === "preview" && previewRow) {
        returnToLatestVersion();
        return;
      }
      if (panel.id !== "preview" && panel.id !== "olderVersion") {
        setPreviewRow(null);
      }
      onPanelChange?.(panel.id);
    },
    [onPanelChange, previewRow, returnToLatestVersion],
  );

  const renderVaultPanel = () => {
    if (detailsLoading && !workspaceResponse) {
      return (
        <div className="qms-doc-workspace__panel">
          <p className="text-muted mb-0 small">Loading workspace…</p>
        </div>
      );
    }

    if (detailsError && !workspaceResponse) {
      return (
        <div className="qms-doc-workspace__panel">
          <p className="text-danger mb-0 small">{detailsError}</p>
        </div>
      );
    }

    switch (activePanel) {
      case "reviewers":
        return (
          <QmsVaultPeoplePanel
            mode="reviewers"
            readOnly
            workspaceResponse={workspaceResponse || {}}
            workflow={readOnlyWorkflow}
          />
        );
      case "approvers":
        return (
          <QmsVaultPeoplePanel
            mode="approvers"
            readOnly
            workspaceResponse={workspaceResponse || {}}
            workflow={readOnlyWorkflow}
          />
        );
      case "quiz":
        return (
          <QmsVaultQuizPanel
            workspaceResponse={workspaceResponse || {}}
            workflow={readOnlyWorkflow}
            userData={userData}
          />
        );
      case "associatedDocs":
        return (
          <QmsVaultAssociatedDocsPanel
            workspaceResponse={workspaceResponse || {}}
            workflow={readOnlyWorkflow}
            onPreviewDocument={openAssociatedDocPreview}
          />
        );
      case "olderVersion":
        return (
          <QmsVaultVersionHistoryPanel
            workspaceResponse={workspaceResponse || {}}
            loading={detailsLoading && !workspaceResponse}
            onOpenVersion={(versionRow) => {
              setPreviewRow(versionRow);
              onPanelChange?.("preview");
            }}
          />
        );
      case "auditLog":
        return (
          <QmsVaultAuditLogPanel
            workspaceResponse={workspaceResponse || {}}
            loading={detailsLoading && !workspaceResponse}
          />
        );
      default:
        return null;
    }
  };

  const renderPanelContent = () => {
    if (activePanel === "libraryStaff") {
      return (
        <QmsDocumentLibraryStaffPanel
          active
          staff={staff}
          loading={staffLoading}
          savingStaffIds={savingStaffIds}
          canManageStaff={canManageStaff}
          onToggleStaff={onToggleStaff}
          onBulkToggleStaff={onBulkToggleStaff}
        />
      );
    }

    if (activePanel === "learningStaff") {
      if (canManageLearningStaff && onMyLearningSave) {
        return (
          <QmsMyLearningAssignmentPanel
            staff={learningStaff}
            staffTrainingAdditionalRoles={learningStaffTrainingRoles}
            documentId={row?.DocumentId}
            loading={learningStaffLoading}
            saving={learningStaffSaving || savingLearningStaffIds.length > 0}
            onSave={onMyLearningSave}
            onDirtyChange={onMyLearningDirtyChange}
            className="qms-publish-ml-staff qms-doc-lib__ml-staff"
          />
        );
      }

      return (
        <QmsDocumentLibraryStaffPanel
          active
          staff={learningStaff}
          loading={learningStaffLoading}
          savingStaffIds={savingLearningStaffIds}
          canManageStaff={canManageLearningStaff}
          assignedOnly={!canManageLearningStaff}
          onToggleStaff={onToggleLearningStaff}
          onBulkToggleStaff={onBulkToggleLearningStaff}
        />
      );
    }

    if (activePanel === "learningProgress") {
      return (
        <QmsDocumentLibraryLearningPanel
          documentRow={row}
          staff={learningProgressStaff}
          loading={learningProgressLoading}
          remindingStaffIds={remindingStaffIds}
          canSendReminders={canSendLearningReminders}
          onSendReminder={onSendLearningReminder}
          onSendBulkReminder={onSendBulkLearningReminder}
        />
      );
    }

    if (isPreviewPanel) {
      return (
        <div className="qms-vault-workspace__panel-main is-preview">
          <div
            className="qms-doc-workspace__preview-pane"
            data-tour="qms-doc-workspace-preview"
          >
            {viewingAssociatedDocument ? (
              <div className="qms-vault-workspace__version-banner is-associated">
                <div className="qms-vault-workspace__version-banner-copy">
                  <i className="fas fa-link" aria-hidden="true" />
                  <span>
                    Previewing associated document{" "}
                    <strong>{getVaultDocumentTitle(previewRow)}</strong>
                  </span>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary qms-vault-workspace__version-banner-btn"
                  onClick={returnToLatestVersion}
                >
                  Back to main document
                </button>
              </div>
            ) : viewingHistoricalVersion ? (
              <div className="qms-vault-workspace__version-banner">
                <div className="qms-vault-workspace__version-banner-copy">
                  <i className="fas fa-history" aria-hidden="true" />
                  <span>
                    Preview only — older version{" "}
                    <strong>v{previewRow?.VersionNumber ?? "—"}</strong>
                    {row?.VersionNumber != null ? (
                      <>
                        {" "}
                        (current is <strong>v{row.VersionNumber}</strong>)
                      </>
                    ) : null}
                  </span>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary qms-vault-workspace__version-banner-btn"
                  onClick={returnToLatestVersion}
                >
                  Back to latest version
                </button>
              </div>
            ) : null}
          <div className="qms-vault-workspace__preview-body">
            {detailsLoading && !resolvedDocumentUrl ? (
              <p className="text-muted mb-0 small px-2 py-3">Loading document preview…</p>
            ) : detailsError && !resolvedDocumentUrl ? (
              <p className="text-danger mb-0 small px-2 py-3">{detailsError}</p>
            ) : (
              <QmsDocumentPreviewPane
                active={isPreviewPanel}
                documentId={
                  previewRow?.DocumentId ||
                  previewRow?.DocumentIdRef ||
                  row?.DocumentId ||
                  row?.DocumentIdRef ||
                  ""
                }
                documentUrl={resolvedDocumentUrl}
                documentExtension={resolvedDocumentExtension}
                documentTitle={getVaultDocumentTitle(previewRow || row) || documentTitle}
              />
            )}
          </div>
          </div>
        </div>
      );
    }

    if (needsVaultData) {
      return renderVaultPanel();
    }

    return null;
  };

  if (!open || !row) return null;

  return (
    <>
      <div
        className="modal fade show d-block qms-doc-workspace-modal qms-vault-workspace-modal qms-doc-lib-workspace-modal"
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label="Document workspace"
        style={{ backgroundColor: "rgba(15, 23, 42, 0.45)" }}
        onClick={onClose}
      >
        <div
          className="modal-dialog qms-doc-workspace-modal__dialog"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="modal-content qms-doc-workspace-modal__content">
            <div className="qms-doc-workspace-modal__header">
              <div className="qms-doc-workspace-modal__header-main">
                <div className="qms-doc-workspace-modal__icon" aria-hidden="true">
                  <i className="fas fa-folder-open" />
                </div>
                <div>
                  <h5 className="qms-doc-workspace-modal__title">
                    {documentTitle || "Document"}
                  </h5>
                  {documentCode ? (
                    <div className="qms-doc-workspace-modal__code">
                      {documentCode}
                    </div>
                  ) : null}
                </div>
              </div>
              <div className="qms-doc-workspace-modal__header-actions">
                <button
                  type="button"
                  className="qms-tour-help-btn qms-doc-workspace-modal__guide-btn"
                  data-tour="qms-doc-workspace-guide"
                  onClick={openPanelGuide}
                  title={`Guide for ${activePanelMeta.label}`}
                >
                  <i className="fas fa-route" aria-hidden="true" />
                  Guide
                </button>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={onClose}
                />
              </div>
            </div>

            <div className="qms-doc-workspace-modal__body">
              <nav
                className="qms-doc-workspace-modal__sidebar"
                aria-label="Document actions"
                data-tour="qms-doc-workspace-nav"
              >
                <ul className="qms-doc-workspace-modal__nav">
                  {panels.map((panel) => {
                    const isActive = activePanel === panel.id;
                    return (
                      <li key={panel.id}>
                        <button
                          type="button"
                          className={`qms-doc-workspace-modal__nav-item${
                            isActive ? " is-active" : ""
                          }`}
                          aria-current={isActive ? "page" : undefined}
                          data-tour={
                            isActive ? "qms-doc-workspace-nav-active" : undefined
                          }
                          onClick={() => handleNavClick(panel)}
                        >
                          <i className={panel.icon} aria-hidden="true" />
                          <span>{panel.label}</span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </nav>

              <div
                className={`qms-doc-workspace-modal__main${
                  isPreviewPanel ? " is-preview" : ""
                }${isStaffPanel ? " is-staff" : ""}${
                  isLearningProgressPanel ? " is-learning-progress" : ""
                }${
                  activePanel === "associatedDocs" ? " is-associated-docs" : ""
                }`}
                data-tour="qms-doc-workspace-panel"
              >
                {renderPanelContent()}
              </div>
            </div>
          </div>
        </div>
      </div>

      <IsfPageTour
        open={showWorkspaceTour}
        steps={workspaceTourSteps}
        staffId={staffId}
        markTourCompleted={handleTourCompleted}
        onClose={handleTourClose}
      />
    </>
  );
}

export default QmsDocumentLibraryWorkspaceModal;
